import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { siteData } from '../data/mock';
import { Menu, X, Flame } from 'lucide-react';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (href) => {
    setIsMobileMenuOpen(false);
    
    // If we're not on home page, navigate there first
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation then scroll
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const goHome = () => {
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-md' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={goHome}>
            <img 
              src={siteData.brand.logo} 
              alt={siteData.brand.name}
              className="h-14 w-auto"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {siteData.navigation.map((item) => (
              <button
                key={item.name}
                onClick={() => handleNavigation(item.href)}
                className="text-[#205781] hover:text-[#98d2c0] transition-colors font-medium text-sm"
              >
                {item.name}
              </button>
            ))}

            {/* Agni Score Button */}
            <Link
              to="/agni-score"
              className="flex items-center gap-1.5 bg-[#f6f8d5] text-[#205781] px-4 py-2 rounded-full text-sm font-medium hover:bg-[#e8eac0] transition-colors border border-[#205781]/30"
            >
              <Flame className="w-4 h-4 text-orange-500" />
              AGNI SCORE™
            </Link>

            {/* Special Offers Button */}
            <Link
              to="/pay"
              className="bg-gradient-to-r from-[#D4A853] to-[#c49a47] text-white px-5 py-2.5 rounded-md text-sm font-semibold hover:shadow-lg transition-all flex items-center gap-2"
            >
              <span className="animate-pulse">🔥</span>
              SPECIAL OFFERS
            </Link>

            {/* Book Consultation Button */}
            <Link
              to="/book-consultation"
              className="bg-[#205781] text-white px-6 py-3 rounded-md text-sm font-semibold hover:bg-[#1a4666] transition-colors"
            >
              BOOK CONSULTATION
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-[#205781]"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-4 py-4 space-y-3">
            {siteData.navigation.map((item) => (
              <button
                key={item.name}
                onClick={() => handleNavigation(item.href)}
                className="block w-full text-left text-[#205781] py-2 hover:text-[#D4A853] transition-colors"
              >
                {item.name}
              </button>
            ))}
            <Link
              to="/agni-score"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center justify-center gap-2 w-full bg-[#f6f8d5] text-[#205781] px-4 py-3 rounded-full text-sm font-medium border border-[#D4A853]"
            >
              <Flame className="w-4 h-4 text-orange-500" />
              AGNI SCORE™
            </Link>
            <Link
              to="/pay"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full text-center bg-gradient-to-r from-[#D4A853] to-[#c49a47] text-white px-4 py-3 rounded-md text-sm font-semibold"
            >
              🔥 SPECIAL OFFERS
            </Link>
            <Link
              to="/book-consultation"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full text-center bg-[#205781] text-white px-4 py-3 rounded-md text-sm font-semibold"
            >
              BOOK CONSULTATION
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
